import { useRef } from "react";
import { motion, useInView } from "framer-motion";

export interface UnderlineOnViewProps {
  children: React.ReactNode;
  /** Underline color (default: 'var(--color-fwd-red)'). */
  color?: string;
  /** Underline thickness in px (default: 3). */
  thickness?: number;
  /** Animation duration in seconds (default: 0.6). */
  duration?: number;
  /** Delay before animation starts in seconds (default: 0). */
  delay?: number;
  /** Vertical offset below the text baseline in px (default: -2). */
  offset?: number;
  /** Highlight color behind the text (default: color at 15% opacity). */
  highlightColor?: string;
}

/**
 * Wraps inline text and draws an animated underline when scrolled into view.
 * The underline sweeps in from left to right using a scaleX transform.
 */
export const UnderlineOnView = ({
  children,
  color = "var(--color-fwd-red)",
  thickness = 3,
  duration = 0.6,
  delay = 0,
  offset = -2,
  highlightColor,
}: UnderlineOnViewProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });

  return (
    <span ref={ref} className="relative inline-block">
      {/* Highlight pen effect behind text */}
      <motion.span
        aria-hidden
        className="absolute inset-0 pointer-events-none rounded-sm"
        style={{
          background:
            highlightColor ?? `color-mix(in srgb, ${color} 15%, transparent)`,
          transformOrigin: "left center",
        }}
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
        transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
      />
      <span className="relative">{children}</span>
      <motion.span
        aria-hidden
        className="absolute left-0 right-0 block pointer-events-none rounded-full"
        style={{
          bottom: offset,
          height: thickness,
          background: color,
          transformOrigin: "left center",
        }}
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
        transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
      />
    </span>
  );
};
